import { TBaseItem, TMultiSelectItem } from "@centricsoftware/design-system";
import { cnl } from "c8-platform";
import { StoreNames } from "csi/store/Names";
import { action, makeObservable, observable, runInAction } from "mobx";
import { queryDataStoreValuesForFormData } from "src/c8-platform/service/dataStoreService";
import { IAggregateStoreResult } from "src/c8-platform/type/aggregateStore";
import { queryAggregateStoreForSelect } from "../selectService";

export type OnLoadOptions = (options: TMultiSelectItem[] | TBaseItem[]) => void;
export type GoToDialogSave = (urls: cnl[]) => void;

export class SelectStoreBase {
	/** store names which includes the aggregate store */
	protected readonly aggregateStore: StoreNames;

	/** enum form data */
	protected readonly enumFormData: FormData;

	/** indicate if error */
	@observable errorText: string = "";

	/** callback for go to dialog save */
	protected readonly goToDialogSave: GoToDialogSave;

	/** invalid items from the store */
	@observable.ref invalidItems: IAggregateStoreResult[] = [];

	/** indicate if loading */
	@observable isLoading: boolean = false;

	/** field label, part of go to dialog title */
	label: string;

	/** callback when options are loaded */
	protected readonly onLoadOptions: OnLoadOptions;

	/** options for the select */
	@observable.ref options: TMultiSelectItem[] | TBaseItem[] = [];

	/** Go to Dialog is allowed if there is a callback to process it and there are valid options */
	get canGoToDialog(): boolean {
		return !!this.goToDialogSave && !!this.options?.length;
	}

	/** callback when select is opened */
	@action onOpen = async (): Promise<void> => {
		const { aggregateStore, enumFormData } = this;
		// if no store, return, options will be empty
		if (!aggregateStore && !enumFormData) {
			return;
		}

		// indicate loading
		this.isLoading = true;
		let options;
		let invalidItems;
		if (enumFormData) {
			// TODO: fix this
			const values = await queryDataStoreValuesForFormData(enumFormData) as any;
			const stringValues = values.VALUES.Value.map((x) => x.Value);
			options = stringValues.map((item: string) => ({
				id: item,
			}));
		} else {
			const { options: opts, invalidItems: inv } = await queryAggregateStoreForSelect(aggregateStore, (text: string) => this.setErrorText(text));
			options = opts;
			invalidItems = inv;
		}

		runInAction(() => {
			// set options to observable array
			this.options = observable.array(options.map((item) => Object.assign(item)));
			this.onLoadOptions?.(this.options);
			this.invalidItems = observable.array(invalidItems?.map((item) => Object.assign(item)));
			this.isLoading = false;
		});
	};

	/** callback when select is closed */
	@action setErrorText = (text: string): void => {
		this.errorText = text;
	};

	constructor(aggregateStore: StoreNames, onLoadOptions: OnLoadOptions, label: string, goToDialogSave?: GoToDialogSave, enumFormData?: FormData, options?: TBaseItem[]) {
		this.onLoadOptions = onLoadOptions;
		this.aggregateStore = aggregateStore;
		this.goToDialogSave = goToDialogSave;
		this.enumFormData = enumFormData;
		this.options = options;
		this.label = label;
		makeObservable(this);
	}
}